import type { AdSessionDto } from '@pxc/shared';

/**
 * AdService (§10).
 *
 * A runner's only job is to *play* an ad and report what the provider gave back.
 * It cannot grant anything — the reward is decided by the backend when the
 * completion is verified. Swapping networks means adding a runner here and
 * setting AD_PROVIDER on the server; nothing else in the app changes.
 */

export interface AdProgress {
  /** Whole seconds left of the ad, for the overlay's countdown. */
  remainingSeconds: number;
  totalSeconds: number;
}

export interface AdRunnerHooks {
  onProgress(progress: AdProgress): void;
  /** Resolves once the ad has actually started rendering. */
  onStarted(): void;
  /** The player tapped inside the ad while it played (Adsgram only), and how that showed. */
  onClicked?(via: string): void;
  /** How long after that tap the player showed being back in the app (ms). */
  onClickDwell?(ms: number): void;
}

export interface AdRunResult {
  providerToken: string | null;
}

export interface AdRunner {
  readonly kind: string;
  run(session: AdSessionDto, hooks: AdRunnerHooks): Promise<AdRunResult>;
}

/** Why our own code could not get an ad, as opposed to the network's words. */
export type AdUnavailableReason = 'noSdk' | 'noBlock' | 'notLoaded' | 'unreachable' | 'noFill';

export class AdUnavailableError extends Error {
  /**
   * `reason` is set when the message is ours, so the screen can say it in the
   * reader's language. The network's own messages carry none and are shown as
   * they come.
   */
  constructor(
    message: string,
    readonly reason?: AdUnavailableReason,
  ) {
    super(message);
    this.name = 'AdUnavailableError';
  }
}

export class AdDismissedError extends Error {
  constructor() {
    super('The ad was closed before it finished.');
    this.name = 'AdDismissedError';
  }
}

/**
 * An interstitial closed before it had been on screen long enough to count
 * (`seconds`, the slot's minimum). A dismissal like any other to the server.
 */
export class AdClosedEarlyError extends AdDismissedError {
  constructor(readonly seconds: number) {
    super();
    this.message = `The ad was closed before ${seconds} seconds.`;
    this.name = 'AdClosedEarlyError';
  }
}

/** The network asked for a pause between ads (Adsgram's "several ads in a row"). Nothing was counted. */
export class AdTooSoonError extends Error {
  constructor() {
    super('The ad network asks for a short pause between ads.');
    this.name = 'AdTooSoonError';
  }
}

/**
 * Development runner. It plays a real countdown in a real overlay and returns
 * the server-signed completion token, so the whole verification path is
 * exercised — this is a stand-in for the network, not for the security model.
 *
 * The server refuses to boot with AD_PROVIDER=mock in production.
 */
export class MockAdRunner implements AdRunner {
  readonly kind = 'mock';

  async run(session: AdSessionDto, hooks: AdRunnerHooks): Promise<AdRunResult> {
    const mock = session.mock;
    if (!mock) {
      throw new AdUnavailableError('The development ad session is missing its completion token.');
    }

    const totalSeconds = Math.max(1, mock.durationSeconds);
    hooks.onStarted();

    await new Promise<void>((resolve) => {
      const startedAt = Date.now();
      const tick = (): void => {
        const elapsed = (Date.now() - startedAt) / 1000;
        const remaining = Math.max(0, Math.ceil(totalSeconds - elapsed));
        hooks.onProgress({ remainingSeconds: remaining, totalSeconds });
        if (elapsed >= totalSeconds) {
          window.clearInterval(timer);
          resolve();
        }
      };
      const timer = window.setInterval(tick, 200);
      tick();
    });

    return { providerToken: mock.completionToken };
  }
}

/**
 * Production runner. Rewarded-ad networks for Telegram Mini Apps inject a global
 * SDK and hand back a completion token or fire a server-to-server postback.
 *
 * With no network configured this throws a precise, actionable error rather than
 * pretending an ad played (§58) — there is no code path here that fabricates a
 * completion.
 */
export class SdkAdRunner implements AdRunner {
  constructor(readonly kind: string) {}

  async run(session: AdSessionDto, hooks: AdRunnerHooks): Promise<AdRunResult> {
    const sdk = (window as unknown as { pxcAdSdk?: PxcAdSdk }).pxcAdSdk;
    if (!sdk || typeof sdk.showRewarded !== 'function') {
      throw new AdUnavailableError(
        `No ad SDK is loaded for provider "${this.kind}". Add the network's script to index.html and expose it as window.pxcAdSdk.`,
        'noSdk',
      );
    }

    hooks.onStarted();
    const outcome = await sdk.showRewarded({
      placement: session.placement,
      sessionId: session.adSessionId,
      onProgress: (remainingSeconds, totalSeconds) =>
        hooks.onProgress({ remainingSeconds, totalSeconds }),
    });

    if (outcome.dismissed) throw new AdDismissedError();
    // A network that uses server-to-server postbacks returns no token; the
    // backend will already have the confirmation when we call `complete`.
    return { providerToken: outcome.token ?? null };
  }
}

/** The shape a network adapter must expose on `window.pxcAdSdk`. */
export interface PxcAdSdk {
  showRewarded(options: {
    placement: string;
    sessionId: string;
    onProgress?: (remainingSeconds: number, totalSeconds: number) => void;
  }): Promise<{ dismissed?: boolean; token?: string | null }>;
}

/**
 * Adsgram (https://docs.adsgram.ai/publisher/api-reference). The SDK is loaded
 * on first use and plays the block the server named for this session. Its
 * result only says the ad ended; whether it counts is decided by the server,
 * which in the default mode waits for Adsgram's own Reward URL call.
 */
export class AdsgramRunner implements AdRunner {
  readonly kind = 'adsgram';

  async run(session: AdSessionDto, hooks: AdRunnerHooks): Promise<AdRunResult> {
    const blockId = session.adsgram?.blockId;
    if (!blockId) throw new AdUnavailableError('The ad session is missing its Adsgram block.', 'noBlock');

    const sdk = await loadAdsgram();
    // One controller per block, kept for the next ad: Adsgram's own examples
    // init once and show many times, and a shift can now take ten in a row.
    let controller = adsgramControllers.get(blockId);
    if (!controller) {
      controller = sdk.init({ blockId });
      adsgramControllers.set(blockId, controller);
    }

    hooks.onStarted();
    const watching = watchAdClick((via) => hooks.onClicked?.(via));
    let result: AdsgramShowResult;
    try {
      result = await controller.show();
    } catch (error) {
      throw adsgramFailure(error);
    } finally {
      const rules = session.adsgram;
      // Adsgram can report the tap a moment after the ad closes.
      if (rules?.clickRequired && !watching.seen()) await new Promise((done) => setTimeout(done, 600));
      // A player who tapped and has not shown being back yet is still with the
      // advertiser, even if the ad already closed: wait for them, but never
      // past the stay the rule asks for.
      if (rules?.clickRequired && rules.clickDwellSeconds && watching.seen()) {
        await watching.backOr(rules.clickDwellSeconds * 1000);
      }
      const dwell = watching.dwellMs();
      if (dwell !== null) hooks.onClickDwell?.(dwell);
      watching.stop();
    }
    // show() resolves only for an ad watched to the end — and also when Adsgram's
    // anti-fraud refuses the view, which is not one to count.
    if (!result.done) throw new AdDismissedError();
    if (/anti-?fraud|антифрод/i.test(result.description ?? '')) throw new AdUnavailableError(result.description);
    return { providerToken: null };
  }
}

/**
 * Adsgram's own report of a tap on the ad's button: the SDK calls
 * api.adsgram.ai/event?type=Click the moment the button is pressed, whatever
 * it opens — a site, a channel, a bot or another Mini App.
 */
const ADSGRAM_CLICK = /adsgram\.ai\/event\b[^#]*[?&]type=click\b/i;

/** Telegram bridge events that open a link. */
const LINK_EVENTS = new Set(['web_app_open_link', 'web_app_open_tg_link']);

function urlOf(input: unknown): string {
  if (typeof input === 'string') return input;
  if (input instanceof URL) return input.href;
  if (typeof Request !== 'undefined' && input instanceof Request) return input.url;
  return '';
}

/**
 * Watches, while an Adsgram ad plays, for the player tapping inside it.
 *
 * The sure sign is Adsgram's own Click event, caught on its way out (fetch,
 * sendBeacon or XHR): it is sent for every kind of button. Opening a link
 * through Telegram, window.open or a plain link counts too. The page hiding
 * does not: a Mini App opened from the ad stacks over this one, which never
 * hides, and leaving the app is no tap. `onClick` hears how the tap showed,
 * which the server keeps for the record.
 */
function watchAdClick(onClick: (via: string) => void): {
  seen(): boolean;
  /** Ms from the tap to the first sign of being back (or to now); null without a tap. */
  dwellMs(): number | null;
  /** Resolves once the player is back, or `ms` after the tap, whichever is first. */
  backOr(ms: number): Promise<void>;
  stop(): void;
} {
  let clickedAt: number | null = null;
  let away = false;
  /** Since the player last left: when they were first back, and whether that is sure yet. */
  let backAt: number | null = null;
  let backSure = false;
  const waiting: Array<() => void> = [];
  const mark = (via: string) => {
    if (clickedAt !== null) return;
    clickedAt = Date.now();
    onClick(via);
  };
  const sure = () => {
    backSure = true;
    for (const resolve of waiting.splice(0)) resolve();
  };

  const undo: Array<() => void> = [];
  const wrap = (
    target: Record<string, unknown> | undefined,
    name: string,
    via: string,
    when?: (args: unknown[]) => boolean,
  ) => {
    const original = target?.[name];
    if (!target || typeof original !== 'function') return;
    const own = Object.prototype.hasOwnProperty.call(target, name);
    try {
      target[name] = function (this: unknown, ...args: unknown[]) {
        try {
          if (!when || when(args)) mark(via);
        } catch {
          // Watching must never break the call it watches.
        }
        return (original as (...params: unknown[]) => unknown).apply(this === undefined ? target : this, args);
      };
      undo.push(() => {
        if (own) target[name] = original;
        else delete target[name];
      });
    } catch {
      // A frozen object keeps its own; the other ways still tell.
    }
  };

  const reportsClick = (url: unknown) => ADSGRAM_CLICK.test(urlOf(url));
  wrap(window as unknown as Record<string, unknown>, 'fetch', 'adsgram-click', (args) => reportsClick(args[0]));
  wrap(navigator as unknown as Record<string, unknown>, 'sendBeacon', 'adsgram-click', (args) => reportsClick(args[0]));
  wrap(XMLHttpRequest.prototype as unknown as Record<string, unknown>, 'open', 'adsgram-click', (args) =>
    reportsClick(args[1]),
  );

  const telegram = (window as unknown as {
    Telegram?: { WebApp?: Record<string, unknown>; WebView?: Record<string, unknown> };
  }).Telegram;
  wrap(telegram?.WebApp, 'openLink', 'openLink');
  wrap(telegram?.WebApp, 'openTelegramLink', 'openTelegramLink');
  wrap(telegram?.WebView, 'postEvent', 'bridge', (args) => typeof args[0] === 'string' && LINK_EVENTS.has(args[0]));
  wrap(window as unknown as Record<string, unknown>, 'open', 'window.open');

  const onLink = (event: MouseEvent) => {
    if (event.composedPath().some((node) => node instanceof HTMLAnchorElement && node.href !== '')) mark('link');
  };
  document.addEventListener('click', onLink, true);

  /*
   * Back in the app, after the tap. A new touch on it is sure: while the
   * advertiser is on screen nothing here can be touched, and the tap's own
   * touch came before. Telegram saying the Mini App is active again, or the
   * page showing again, is sure once it has held a second — opening a link can
   * flick the app away and back before the link itself takes the screen — and
   * leaving again undoes it.
   */
  const onTouch = () => {
    if (clickedAt === null || Date.now() - clickedAt <= 400) return;
    if (away || backAt === null) backAt = Date.now();
    away = false;
    sure();
  };
  const leave = () => {
    if (clickedAt === null) return;
    away = true;
    backAt = null;
    backSure = false;
  };
  const comeBack = () => {
    if (!away) return;
    away = false;
    const at = Date.now();
    backAt = at;
    setTimeout(() => {
      if (!away && backAt === at) sure();
    }, 1000);
  };
  const onVisibility = () => (document.visibilityState === 'hidden' ? leave() : comeBack());
  document.addEventListener('pointerdown', onTouch, true);
  document.addEventListener('touchstart', onTouch, true);
  document.addEventListener('visibilitychange', onVisibility);
  const events = telegram?.WebApp as
    | { onEvent?: (name: string, handler: () => void) => void; offEvent?: (name: string, handler: () => void) => void }
    | undefined;
  events?.onEvent?.('deactivated', leave);
  events?.onEvent?.('activated', comeBack);

  return {
    seen: () => clickedAt !== null,
    dwellMs: () => (clickedAt === null ? null : (away || backAt === null ? Date.now() : backAt) - clickedAt),
    backOr: (ms) =>
      new Promise<void>((resolve) => {
        if (clickedAt === null || (backSure && !away)) return resolve();
        const left = clickedAt + ms - Date.now();
        if (left <= 0) return resolve();
        waiting.push(resolve);
        setTimeout(resolve, left);
      }),
    stop: () => {
      document.removeEventListener('click', onLink, true);
      document.removeEventListener('pointerdown', onTouch, true);
      document.removeEventListener('touchstart', onTouch, true);
      document.removeEventListener('visibilitychange', onVisibility);
      events?.offEvent?.('deactivated', leave);
      events?.offEvent?.('activated', comeBack);
      for (const resolve of waiting.splice(0)) resolve();
      for (const restore of undo) restore();
    },
  };
}

/**
 * What a rejected `show()` means (docs.adsgram.ai/publisher/api-reference#show).
 *
 * The SDK rejects with one shape for very different things, and only `state`
 * and `error` tell them apart: the user closing an ad that was playing is not
 * "there is no ad to show", and neither is the network asking for a pause
 * between ads. Reading every `done: false` as a close told users they had
 * skipped an ad that never appeared.
 */
export function adsgramFailure(error: unknown): Error {
  const outcome = (typeof error === 'object' && error !== null ? error : {}) as Partial<AdsgramShowResult>;
  const said = error instanceof Error ? error.message : (outcome.description ?? '');
  if (outcome.state === 'load') {
    if (/in a row/i.test(said)) return new AdTooSoonError();
    // Nothing to show is ours to explain; a load *error* is a setup problem
    // (wrong app URL, inactive block…) and the SDK's words say what to fix.
    return new AdUnavailableError(outcome.error === true ? said : '');
  }
  if (outcome.done === false && outcome.error !== true) return new AdDismissedError();
  return new AdUnavailableError(said);
}

interface AdsgramShowResult {
  done: boolean;
  description: string;
  state: 'load' | 'render' | 'playing' | 'destroy';
  error: boolean;
}

interface AdsgramController {
  show(): Promise<AdsgramShowResult>;
  /** Adsgram's events (onStart: "the first banner frame is shown"); optional, so an older SDK still plays. */
  addEventListener?(event: string, callback: () => void): void;
  removeEventListener?(event: string, callback: () => void): void;
}

interface AdsgramSdk {
  init(options: { blockId: string }): AdsgramController;
}

const adsgramControllers = new Map<string, AdsgramController>();

const ADSGRAM_SDK_URL = 'https://sad.adsgram.ai/js/sad.min.js';
let adsgramLoading: Promise<AdsgramSdk> | null = null;

function loadAdsgram(): Promise<AdsgramSdk> {
  const existing = (window as unknown as { Adsgram?: AdsgramSdk }).Adsgram;
  if (existing) return Promise.resolve(existing);
  if (adsgramLoading) return adsgramLoading;

  adsgramLoading = new Promise<AdsgramSdk>((resolve, reject) => {
    const script = document.createElement('script');
    script.src = ADSGRAM_SDK_URL;
    script.async = true;
    script.onload = () => {
      const sdk = (window as unknown as { Adsgram?: AdsgramSdk }).Adsgram;
      if (sdk) resolve(sdk);
      else reject(new AdUnavailableError('The ad network did not load.', 'notLoaded'));
    };
    script.onerror = () => {
      adsgramLoading = null;
      reject(new AdUnavailableError('The ad network could not be reached.', 'unreachable'));
    };
    document.head.appendChild(script);
  });
  return adsgramLoading;
}

/** Adsgram's SDK fetched ahead of an ad, so the ad itself starts sooner; rejects as `loadAdsgram` does. */
export async function preloadAdsgram(): Promise<void> {
  await loadAdsgram();
}

/**
 * Plays one Adsgram interstitial (an `int-` block) and says how long it was on
 * screen, in ms: from its first frame (Adsgram's onStart; from the call, on an
 * SDK without events) to its closing.
 *
 * An interstitial is not a rewarded ad: the player may close it before it
 * ends, and Adsgram's show() then settles as for one watched to the end
 * (docs.adsgram.ai/publisher/reward-interstitial-integration). Closing is
 * therefore an outcome here, not a failure — whether the time is enough is
 * the slot's rule (adFlow.ts, watchInterstitialAd). Rejects only when there was
 * no ad to show, the network asked for a pause, it failed to play, or
 * Adsgram's anti-fraud refused the view: the rewarded runner's errors.
 */
export async function playAdsgramInterstitial(
  blockId: string,
  hooks: { onStarted?: () => void } = {},
): Promise<{ shownMs: number }> {
  if (!blockId) throw new AdUnavailableError('The ad session is missing its Adsgram block.', 'noBlock');
  const sdk = await loadAdsgram();
  let controller = adsgramControllers.get(blockId);
  if (!controller) {
    controller = sdk.init({ blockId });
    adsgramControllers.set(blockId, controller);
  }

  let startedAt: number | null = null;
  const onStart = () => {
    startedAt ??= Date.now();
  };
  controller.addEventListener?.('onStart', onStart);
  const calledAt = Date.now();
  hooks.onStarted?.();
  try {
    let result: AdsgramShowResult | null = null;
    try {
      result = await controller.show();
    } catch (error) {
      const failure = adsgramFailure(error);
      // Closed: the time on screen decides. Anything else is not an ad seen.
      if (!(failure instanceof AdDismissedError)) throw failure;
    }
    if (result) {
      // Settled, but not for an ad seen: a playing error, or Adsgram's anti-fraud refusing the view.
      if (result.error === true) throw new AdUnavailableError(result.description ?? '');
      if (/anti-?fraud|антифрод/i.test(result.description ?? '')) throw new AdUnavailableError(result.description);
    }
    return { shownMs: Date.now() - (startedAt ?? calledAt) };
  } finally {
    controller.removeEventListener?.('onStart', onStart);
  }
}

export function createAdRunner(provider: string): AdRunner {
  if (provider === 'mock') return new MockAdRunner();
  if (provider === 'adsgram') return new AdsgramRunner();
  return new SdkAdRunner(provider);
}
